import React from "react";
import { Box } from "@mui/material";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import BasePlot from "./BasePlot";

const Plot = createPlotlyComponent(Plotly);

export interface BarChartProps {
  data: any[];
  title?: string;
  className?: string;
  plotRef?: string;
  isFullscreen?: boolean;
}

const BarChart = ({
  data,
  title = "Filtering Summary",
  className = "",
  plotRef,
  isFullscreen = false,
}: BarChartProps): React.ReactElement => {
  const COLOR_BEFORE = "#1b9e77";
  const COLOR_AFTER = "#7570b3";
  const findMeasure = (name: string) =>
    data.find((row) => String(row?.Measure ?? "").toLowerCase() === name.toLowerCase());
  const counts = (row: any) => [Number(row?.Before) || 0, Number(row?.After) || 0];
  const cellCounts = counts(findMeasure("Cells"));
  const geneCounts = counts(findMeasure("Genes"));
  const axisRange = (values: number[]) => [0, Math.max(1, ...values) * 1.16];

  // Keep cells and genes on independent scales so both comparisons remain readable.
  const chartData: any[] = [
    {
      type: "bar",
      x: ["Before", "After"],
      y: cellCounts,
      xaxis: "x",
      yaxis: "y",
      marker: { color: [COLOR_BEFORE, COLOR_AFTER] },
      text: cellCounts.map((value) => value.toLocaleString()),
      textposition: "outside",
      cliponaxis: false,
      hovertemplate: "%{x}<br>Cells: %{y:,}<extra></extra>",
      showlegend: false,
    },
    {
      type: "bar",
      x: ["Before", "After"],
      y: geneCounts,
      xaxis: "x2",
      yaxis: "y2",
      marker: { color: [COLOR_BEFORE, COLOR_AFTER] },
      text: geneCounts.map((value) => value.toLocaleString()),
      textposition: "outside",
      cliponaxis: false,
      hovertemplate: "%{x}<br>Genes: %{y:,}<extra></extra>",
      showlegend: false,
    },
  ];

  const commonAxis = {
    fixedrange: true,
    showgrid: true,
    gridcolor: "#eef2f7",
    zeroline: false,
    tickformat: ",d",
    automargin: true,
  };
  const layout: any = {
    autosize: true,
    margin: { l: 58, r: 28, t: 52, b: 46 },
    bargap: 0.38,
    xaxis: {
      domain: [0, 0.44],
      anchor: "y",
      fixedrange: true,
      automargin: true,
    },
    xaxis2: {
      domain: [0.56, 1],
      anchor: "y2",
      fixedrange: true,
      automargin: true,
    },
    yaxis: {
      ...commonAxis,
      title: "Number of cells",
      domain: [0, 1],
      range: axisRange(cellCounts),
    },
    yaxis2: {
      ...commonAxis,
      title: "Number of genes",
      domain: [0, 1],
      anchor: "x2",
      range: axisRange(geneCounts),
    },
    annotations: [
      { text: "Cells", x: 0.22, y: 1.1, xref: "paper", yref: "paper", showarrow: false, font: { size: 16 } },
      { text: "Genes", x: 0.78, y: 1.1, xref: "paper", yref: "paper", showarrow: false, font: { size: 16 } },
    ],
    uniformtext: { mode: "show", minsize: 12 },
    plot_bgcolor: "white",
    paper_bgcolor: "white",
    hovermode: "closest",
  };

  const config = {
    displayModeBar: false,
    responsive: true,
  };

  const barChart = (
    <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
      <Plot
        data={chartData}
        layout={layout}
        config={config}
        style={{ width: "100%", height: "100%", minHeight: "280px" }}
        className={plotRef ? plotRef.replace("#", "") : "plotly-plot"}
        useResizeHandler={true}
      />
    </Box>
  );

  return (
    <BasePlot
      title={title}
      className={className}
      showColorPicker={false}
      downloadFilename={title.toLowerCase().replace(/\s+/g, "_") + "_bar_chart"}
      plotType="bar"
      plotProps={{
        data,
      }}
      isFullscreen={isFullscreen}
    >
      {barChart}
    </BasePlot>
  );
};

export default BarChart;
